import { useTranslation } from 'react-i18next'
import { useAuth } from '@wehunt/core-web'
import { Icon } from '@wehunt/ui'
import { replayEntryVisible } from './replayEntryVisibility'

export interface ReplayHuntButtonProps {
  /** Fires when pressed. The host supplies the navigation. */
  onOpen: () => void
  /** The hunt's start, ISO 8601, as the host already holds it. */
  huntStartTime: string | null
  /**
   * The hunt's attendance roster. The host passes it rather than this component
   * fetching it: the ended-hunt report has already read the hunt, and a second
   * request for a roster it holds would be a round trip to answer a question
   * that is already answered on screen.
   */
  attendanceUserIds: readonly string[]
}

/**
 * The "Replay hunt" entry point rendered into the ended hunt detail page.
 *
 * It takes a callback rather than navigating itself: the page it appears on belongs
 * to clubhouse, and a component that reached for campfire's route from inside
 * another domain's page would put the two domains back in each other's business.
 * The shell wires the callback, because the shell is the only place allowed to know
 * both.
 *
 * It renders NOTHING for a hunt whose replay cannot work — see
 * `replayEntryVisible`. That rule is mobile's, ported rather than reinvented:
 * both clients read one archive from one service, so a hunt mobile will not
 * offer a replay for must not be offered one here. Until this gate existed, web
 * showed the button on every ended hunt to every team member, and the users who
 * clicked it got "No replay for this hunt" and reported a bug.
 */
export function ReplayHuntButton({
  onOpen,
  huntStartTime,
  attendanceUserIds,
}: ReplayHuntButtonProps) {
  const { t } = useTranslation('replay')
  const { state } = useAuth()
  const currentUserId = state.status === 'signed-in' ? state.user.uid : null

  if (!replayEntryVisible({ huntStartTime, attendanceUserIds, currentUserId })) {
    return null
  }

  return (
    <button
      type="button"
      className="wh-btn wh-btn-primary"
      onClick={onOpen}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
    >
      <Icon name="play" size={16} filled />
      {t('openReplay')}
    </button>
  )
}
